/**
 * CallRail Field-Selection Allowlist (Story 22.1, AC #4, AC #12)
 *
 * CallRail's `/v3/a/{account_id}/calls.json` endpoint supports an OPTIONAL
 * `fields=...` query parameter for field selection. Per
 * `apidocs.callrail.com/` verified 2026-05-14:
 *
 *  - The DEFAULT field list (when `?fields=` is omitted) is a small subset
 *    of common fields: `answered, business_phone_number, customer_city,
 *    customer_country, customer_name, customer_phone_number, customer_state,
 *    direction, duration, id, recording, recording_duration, recording_player,
 *    start_time, tracking_phone_number, voicemail`.
 *  - The FULL field list (38+ fields) requires explicit `?fields=` request.
 *  - Conversation Intelligence fields (`sentiment`, `transcription`,
 *    `conversational_transcript`, `call_summary`, `lead_score`,
 *    `lead_score_explanation`, `keywords_spotted`, `call_highlights`,
 *    `voicemail_transcription`) require both `?fields=` AND a Premium CI
 *    subscription. Requesting them without entitlement returns 422 per
 *    `support.callrail.com/hc/en-us/articles/12132320044173-HTTP-response-codes`.
 *
 * Story 22.1's adapter fetches the FULL Story-22.1 default allowlist
 * (38 fields) when `conversationIntelligenceEntitled === true`, and a
 * REDUCED list (29 fields — strips the 9 CI-gated fields) when entitlement
 * is false. This saves bandwidth AND prevents 422 errors on
 * subscription-gated fields.
 *
 * **Joining contract:** fields MUST be joined with `,` (comma) — NEVER with
 * `, ` (comma + space). CallRail's URL parser treats the literal space as
 * part of the next field name and 422s on unrecognized field names.
 *
 * **Web research divergence (Story 22.1 implementation note):** the docs
 * confirm that PII fields (customer_phone_number, customer_name,
 * customer_email, customer_city, customer_state, customer_country) AND the
 * `recording` URL field are in the DEFAULT response — they flow back even
 * without `?fields=`. This means the `sanitizeCallRailPii` helper must run
 * on EVERY record, not just records produced by an opt-in field list.
 */

/**
 * Full Story-22.1 default allowlist for `/calls.json`. Captures the
 * COMPLETE documented field set from `apidocs.callrail.com/` verified
 * 2026-05-14 EXCEPT a small set of fields deliberately deferred to
 * Story 22.5 (recording-link surface) which are stripped at the
 * transformer.
 *
 * Story 22.1 expands the allowlist past the original AC #4 list (38
 * fields) to ship at 100% catalogue parity per the build-to-sell
 * directive — every documented metric / dimension the API exposes
 * should be available to agency users on day one of the integration.
 * The remaining catalogued rows are genuinely separate features
 * (SMS endpoint family, agent endpoint family, recording-signed-URL
 * route) deferred to future Stories.
 *
 * Ordering matches the per-endpoint field table in
 * `_bmad/bmm/data/integration-metrics/callrail.md`. Adding a new field
 * REQUIRES adding it to this allowlist + adding it to the catalogue
 * file's "Story Reference" column for traceability.
 *
 * **NOTE: snake_case** — these are the wire-format field names CallRail
 * expects on the URL. The transformer in `transformers.ts` rewrites them
 * to lowerCamelCase for the ProvenLeap record schema.
 */
export const CALLRAIL_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  // Identity / customer
  "person_id",
  "prior_calls",
  "first_call",
  "good_lead_call_id",
  "good_lead_call_time",
  // Call attributes
  "direction",
  "answer_status",
  "duration",
  "formatted_duration",
  "recording_duration",
  "value",
  "formatted_value",
  "note",
  "voicemail",
  // Marketing attribution
  "source",
  "source_name",
  "medium",
  "campaign",
  "keywords",
  // UTM params
  "utm_source",
  "utm_medium",
  "utm_content",
  "utm_campaign",
  "utm_term",
  "ga",
  // Click IDs
  "gclid",
  "fbclid",
  "msclkid",
  // Landing-page context
  "landing_page_url",
  "referring_url",
  "referrer_domain",
  "last_requested_url",
  // Customer geography (PII-flagged at the transformer)
  "customer_city",
  "customer_state",
  "customer_country",
  "formatted_customer_location",
  "zip_code",
  // Device / channel
  "device_type",
  "call_type",
  "formatted_call_type",
  // Conversation Intelligence — gated by `conversationIntelligenceEntitled`
  "call_summary",
  "sentiment",
  "transcription",
  "conversational_transcript",
  "lead_score",
  "lead_score_explanation",
  "keywords_spotted",
  "call_highlights",
  "speaker_percent",
  // Quality
  "lead_status",
  "tags",
  "tag_level",
  "agent_email",
  "agent_id",
  "agent_name",
  // Tracker
  "tracking_phone_number",
  "formatted_tracking_phone_number",
  "tracker_id",
  "initial_tracker_id",
  "current_tracker_id",
  // Company scope (returned when group_by / company filtering applied)
  "company_id",
  "company_name",
  "company_time_zone",
  // Timing
  "start_time",
  "created_at",
  // Forensics
  "timeline_url",
  "milestones",
  "session_uuid",
  "touchpoint_type",
  // Form submissions (returned on /form_submissions endpoint records)
  "form_name",
  "form_url",
] as const;

/**
 * The 9 Conversation Intelligence fields stripped from `CALLRAIL_FIELD_ALLOWLIST`
 * when `metadata.conversationIntelligenceEntitled === false`. Defined as a
 * Set for O(1) `.has()` lookups in `computeCallRailFields`.
 *
 * Per `support.callrail.com/hc/en-us/articles/35110502230157-Getting-started-with-Premium-Conversation-Intelligence`
 * verified 2026-05-14, these fields require the Premium Conversation
 * Intelligence subscription. Requesting them without entitlement returns
 * 422 (per the HTTP response codes article — 422 = "validation error" which
 * we interpret as field-not-allowed-for-this-subscription).
 *
 * **Round 4 Acceptance Auditor finding (d) carry-forward.**
 */
export const CALLRAIL_CI_GATED_FIELDS: ReadonlySet<string> = new Set<string>([
  "call_summary",
  "sentiment",
  "transcription",
  "conversational_transcript",
  "lead_score",
  "lead_score_explanation",
  "keywords_spotted",
  "call_highlights",
  "speaker_percent",
  // `voicemail_transcription` is computed from `transcription` when
  // voicemail=true — included implicitly via the transcription field
  // strip. Not a separate API field.
]);

/**
 * Compute the `fields=` URL-param value given the integration's CI
 * entitlement state. Returns a comma-joined string (NO spaces).
 *
 * Pure function — safe to call repeatedly; tests assert the exact join
 * shape via the co-located `fields.test.ts`.
 *
 * @param conversationIntelligenceEntitled - Read from `metadata.conversationIntelligenceEntitled`
 *   at the route boundary (set at connect time per AC #2 probe).
 * @returns Comma-joined field list with NO spaces.
 */
export function computeCallRailFields(
  conversationIntelligenceEntitled: boolean,
): string {
  const fields = conversationIntelligenceEntitled
    ? CALLRAIL_FIELD_ALLOWLIST
    : CALLRAIL_FIELD_ALLOWLIST.filter(
        (field) => !CALLRAIL_CI_GATED_FIELDS.has(field),
      );
  // CRITICAL: `.join(",")` NOT `.join(", ")`. CallRail's URL parser treats
  // a literal space as part of the next field name and 422s on
  // unrecognized field names. The co-located test fuzzes against accidental
  // space insertion.
  return fields.join(",");
}

/**
 * Field-selection allowlist for the `/calls/summary.json` endpoint —
 * documented filter params per `apidocs.callrail.com/` verified 2026-05-14.
 *
 * Although `summary.json` does NOT require explicit `?fields=` for output,
 * it DOES accept a number of filter query params that mirror the
 * `/calls.json` field shape — `device`, `min_duration`, `max_duration`,
 * `tags`, `tracker_ids`, `direction`, `answer_status`, `first_time_callers`,
 * `lead_status`, `agent`. Listing them here as a frozen reference Set lets
 * the URL-builder validate that callers passed a known filter key (catches
 * typos like `min_duraton` → 422 at request time).
 */
export const CALLRAIL_SUMMARY_FILTER_KEYS: ReadonlySet<string> = new Set<string>([
  "device",
  "min_duration",
  "max_duration",
  "tags",
  "tracker_ids",
  "direction",
  "answer_status",
  "first_time_callers",
  "lead_status",
  "agent",
]);

/**
 * Documented `group_by` values for `/calls/summary.json` and
 * `/calls/timeseries.json` per `apidocs.callrail.com/` verified 2026-05-14.
 *
 * Includes `company` and `company_id` (CallRail accepts both for the
 * company grouping; the helpers normalize to whichever the docs prefer).
 * Story 22.1 AC #12's Round 5 Edge Case #2 invariant — when the
 * integration's `metadata.companyId` filter is active, `group_by=company`
 * collapses to a single bucket and is rejected at the adapter level.
 */
export const CALLRAIL_GROUP_BY_VALUES: ReadonlySet<string> = new Set<string>([
  "source",
  "keywords",
  "campaign",
  "referrer",
  "landing_page",
  "form_name",
  "company",
  "company_id",
]);

/**
 * Documented `granularity` values for `/calls/timeseries.json` per
 * `apidocs.callrail.com/` verified 2026-05-14.
 *
 * **Web research divergence from the story AC text:** the AC #4 enumerates
 * only `day | week | month`, but the docs ALSO include `hour` and `year`.
 * The adapter accepts the full enum; widgets that bind to the
 * `granularity` selector will get the full set.
 *
 * The default — when `granularity` is omitted — is `day` per the docs.
 */
export const CALLRAIL_GRANULARITY_VALUES: ReadonlySet<string> = new Set<string>([
  "hour",
  "day",
  "week",
  "month",
  "year",
]);

/**
 * **Web research divergence from the story AC text:** per `apidocs.callrail.com/`
 * verified 2026-05-14, the `/calls/timeseries.json` endpoint is capped at
 * **200 data points** per response. The helper at `fetchCallRailCallsTimeseries`
 * surfaces this ceiling via a warning when a granularity/date-range combination
 * would exceed 200 rows — the caller can chunk the date range or use a coarser
 * granularity.
 *
 * Example: 365 days @ daily granularity = 365 rows, exceeds 200 → warn or
 * chunk. 52 weeks @ weekly = 52 rows, fits. 12 months @ monthly = 12 rows,
 * fits. The 200-cap is the realistic edge case for daily granularity on
 * year-plus windows.
 */
export const CALLRAIL_TIMESERIES_MAX_POINTS = 200;
